import {
  getUnitsLabel,
  sortByNearestDate,
  unitsFormatter,
} from '@deckee/utilities';
import { DateTime } from 'luxon';
import {
  extendTimeSeriesToDomainEnd,
  formatClock,
  valueAtTime,
} from '../../../../shared/charts/chart-utils';
import type {
  AxisLabelRow,
  ChartAnnotation,
  TimeSeriesPoint,
} from '../../../../shared/charts/types';
import { CHART_LAYOUT } from '../../../../shared/charts/chart-layout';
import {
  getForecastDayChartDomain,
  isIsoTimeInForecastDayChartDomain,
} from '../day/chartDomain';
import { FREE_FORECAST_DAYS } from '../forecastHorizon';

export const WEATHER_FORECAST_GRAPH_DAYS = FREE_FORECAST_DAYS;

export interface WeatherForecastGraphPoint {
  x: string;
  y: number;
  direction?: number;
  directionText?: string;
  feelsLike?: number;
  /** True when this hourly sample was interpolated between API readings. */
  generated?: boolean;
}

export {
  fillHourlyGapsForWeatherGraphType,
  fillHourlyGapsInGraphPoints,
  safelyFillHourlyGapsForWeatherGraphType,
} from './fillHourlyGraphGaps';

export interface WeatherForecastGraphLocation {
  name?: string;
  distance?: number;
  distanceUnits?: string;
  timeZone: string;
  utcOffset?: number;
}

export interface WeatherForecastGraphApiResponse {
  points?: WeatherForecastGraphPoint[];
  location?: WeatherForecastGraphLocation;
  footerText?: string;
}

interface UnitsConfig {
  windSpeed?: string;
  swellHeight?: string;
  tideHeight?: string;
  temperature?: string;
  distance?: string;
  [key: string]: unknown;
}

export interface EnrichedGraphSeriesPoint {
  time: Date;
  value: number;
  direction?: number;
  directionText?: string;
  feelsLike?: number;
}

const DIRECTION_ARROWS = ['↑', '↗', '→', '↘', '↓', '↙', '←', '↖'] as const;

/** Wind graph API values are m/s; charts and risk use km/h (legacy conversion). */
export const convertWindSpeedFromApi = (metresPerSecond: number): number =>
  metresPerSecond * 3.6;

export const directionDegreesToArrow = (degrees: number): string => {
  const normalized = ((degrees % 360) + 360) % 360;
  const index = Math.round(normalized / 45) % 8;
  return DIRECTION_ARROWS[index];
};

/** Matches `@deckee/utilities` windSpeed knots multiplier (km/h → knots). */
const KNOTS_PER_KMH = 0.539956803455724;

export const WIND_DIRECTION_ARROW_KNOTS_THRESHOLD = 15;

export const windSpeedKmhToKnots = (speedKmh: number): number =>
  speedKmh * KNOTS_PER_KMH;

export const getWindDirectionArrowVariant = (
  speedKmh: number,
): 'thick' | 'thin' =>
  windSpeedKmhToKnots(speedKmh) > WIND_DIRECTION_ARROW_KNOTS_THRESHOLD
    ? 'thick'
    : 'thin';

export const SWELL_DIRECTION_ARROW_PERIOD_THRESHOLD = 10;

export const getSwellDirectionArrowVariant = (
  periodSeconds: number,
): 'thick' | 'thin' =>
  periodSeconds > SWELL_DIRECTION_ARROW_PERIOD_THRESHOLD ? 'thick' : 'thin';

/** Default arrow artwork points right; meteorological degrees use 90° = east. */
export const directionDegreesToRotation = (degrees: number): number => {
  const normalized = ((degrees % 360) + 360) % 360;
  return normalized - 90;
};

/**
 * Wind and swell directions are reported meteorologically: the bearing the flow
 * comes FROM. The chart arrows should point the way the wind/swell is heading,
 * so rotate to the opposite bearing (from + 180°).
 */
export const flowDirectionDegreesToRotation = (fromDegrees: number): number =>
  directionDegreesToRotation(fromDegrees + 180);

export const graphPointsToEnrichedSeries = (
  points: WeatherForecastGraphPoint[],
  selectedDate: string,
  timeZone: string,
  transformValue: (value: number) => number = (value) => value,
): EnrichedGraphSeriesPoint[] =>
  points
    .filter((point) =>
      isIsoTimeInForecastDayChartDomain(point.x, selectedDate, timeZone),
    )
    .map((point) => {
      const pointDateTime = DateTime.fromISO(point.x, { zone: timeZone });
      if (!pointDateTime.isValid) {
        return null;
      }

      const enrichedPoint: EnrichedGraphSeriesPoint = {
        time: pointDateTime.toJSDate(),
        value: transformValue(point.y),
        direction: point.direction,
        directionText: point.directionText,
        feelsLike: point.feelsLike,
      };
      return enrichedPoint;
    })
    .filter((point): point is EnrichedGraphSeriesPoint => point != null)
    .sort((a, b) => a.time.getTime() - b.time.getTime());

export const enrichedSeriesToTimeSeries = (
  series: EnrichedGraphSeriesPoint[],
  domain?: { start: Date; end: Date },
): TimeSeriesPoint[] => {
  const points = series.map((point) => ({
    time: point.time,
    value: point.value,
  }));

  return domain ? extendTimeSeriesToDomainEnd(points, domain) : points;
};

export const getClosestEnrichedPoint = (
  series: EnrichedGraphSeriesPoint[],
  time: Date,
): EnrichedGraphSeriesPoint | undefined => {
  if (series.length === 0) {
    return undefined;
  }

  const pointsToSort = [...series];
  pointsToSort.sort(
    sortByNearestDate(time, (point: EnrichedGraphSeriesPoint) => point.time),
  );

  return pointsToSort[0];
};

export const getClosestGraphPointByIso = (
  points: WeatherForecastGraphPoint[],
  selectedIsoTime: string,
  timeZone: string,
): EnrichedGraphSeriesPoint | undefined => {
  const selectedTime = DateTime.fromISO(selectedIsoTime, { zone: timeZone });
  if (!selectedTime.isValid) {
    return undefined;
  }

  const series = graphPointsToEnrichedSeries(
    points,
    selectedTime.toISODate() ?? '',
    timeZone,
  );
  return getClosestEnrichedPoint(series, selectedTime.toJSDate());
};

export const buildDirectionLabelRow = (
  series: EnrichedGraphSeriesPoint[],
  color: string,
): AxisLabelRow => ({
  key: 'direction',
  color,
  fontSize: 13,
  getLabel: (tick) => {
    const nearest = getClosestEnrichedPoint(series, tick);
    if (nearest?.direction != null) {
      return directionDegreesToArrow(nearest.direction);
    }

    return '';
  },
});

export const buildWindDirectionLabelRow = (
  series: EnrichedGraphSeriesPoint[],
  color: string,
): AxisLabelRow => ({
  key: 'direction',
  color,
  getIcon: (tick) => {
    const nearest = getClosestEnrichedPoint(series, tick);
    if (nearest?.direction == null) {
      return null;
    }

    return {
      variant: getWindDirectionArrowVariant(nearest.value),
      rotation: flowDirectionDegreesToRotation(nearest.direction),
    };
  },
});

export const buildSwellDirectionLabelRow = (
  directionSeries: EnrichedGraphSeriesPoint[],
  periodSeries: EnrichedGraphSeriesPoint[],
  color: string,
): AxisLabelRow => ({
  key: 'direction',
  color,
  getIcon: (tick) => {
    const nearestDirection = getClosestEnrichedPoint(directionSeries, tick);
    if (nearestDirection?.direction == null) {
      return null;
    }

    const nearestPeriod = getClosestEnrichedPoint(periodSeries, tick);
    const periodSeconds = nearestPeriod?.value ?? 0;

    return {
      variant: getSwellDirectionArrowVariant(periodSeconds),
      rotation: flowDirectionDegreesToRotation(nearestDirection.direction),
    };
  },
});

export const buildValueLabelRow = (
  series: TimeSeriesPoint[],
  formatValue: (value: number) => string,
  options:
    | string
    | {
        color?: string;
        textAngle?: number;
        rowHeight?: number;
      } = '#9B9B9B',
): AxisLabelRow => {
  const resolved = typeof options === 'string' ? { color: options } : options;

  return {
    key: 'value',
    color: resolved.color ?? '#9B9B9B',
    textAngle: resolved.textAngle,
    rowHeight: resolved.rowHeight,
    getLabel: (tick) => formatValue(valueAtTime(series, tick)),
  };
};

export const buildTideEventAnnotations = (
  points: WeatherForecastGraphPoint[],
  selectedDate: string,
  timeZone: string,
): ChartAnnotation[] =>
  points
    .filter(
      (point) =>
        isIsoTimeInForecastDayChartDomain(point.x, selectedDate, timeZone) &&
        (point.directionText === 'High' || point.directionText === 'Low'),
    )
    .map((point) => {
      const time = DateTime.fromISO(point.x, { zone: timeZone }).toJSDate();

      return {
        time,
        label: formatClock(time, timeZone),
        position:
          point.directionText === 'High'
            ? ('above' as const)
            : ('below' as const),
      };
    })
    .sort((a, b) => a.time.getTime() - b.time.getTime());

export const getTideTrendLabel = (
  series: TimeSeriesPoint[],
  selectedTime: Date | null,
): string | null => {
  if (!selectedTime || series.length < 2) {
    return null;
  }

  const epsilonMs = 60 * 1000;
  const currentValue = valueAtTime(series, selectedTime);
  const laterValue = valueAtTime(
    series,
    new Date(selectedTime.getTime() + epsilonMs),
  );

  if (laterValue > currentValue) {
    return 'Rising';
  }

  if (laterValue < currentValue) {
    return 'Falling';
  }

  return null;
};

/** Wind speed is shown to the nearest whole number across the forecast. */
const roundWindSpeedValue = (
  value: number,
  unitsConfig: UnitsConfig,
): number | null => {
  const numericValue = unitsFormatter(
    value,
    'windSpeed',
    unitsConfig,
    null,
    true,
  );
  if (typeof numericValue !== 'number' || !Number.isFinite(numericValue)) {
    return null;
  }

  return Math.round(numericValue);
};

const formatWindSpeedKnotsCompact = (
  value: number,
  unitsConfig: UnitsConfig,
): string | null => {
  const numericValue = roundWindSpeedValue(value, unitsConfig);
  if (numericValue == null) {
    return null;
  }

  return `${numericValue}kn`;
};

/** Whole-number wind speed with its unit suffix (e.g. `13km/h`); knots use `kn`. */
const formatWindSpeedValueWithUnit = (
  value: number,
  unitsConfig: UnitsConfig,
): string | null => {
  if (unitsConfig.windSpeed === 'knots') {
    return formatWindSpeedKnotsCompact(value, unitsConfig);
  }

  const numericValue = roundWindSpeedValue(value, unitsConfig);
  if (numericValue == null) {
    return null;
  }

  const label = getUnitsLabel('windSpeed', unitsConfig);
  return `${numericValue}${typeof label === 'string' ? label : ''}`;
};

export const formatWindSpeedDisplay = (
  metresPerSecond: number,
  unitsConfig: UnitsConfig,
  directionText?: string,
): string => {
  const converted = convertWindSpeedFromApi(metresPerSecond);
  const speedDisplay =
    formatWindSpeedValueWithUnit(converted, unitsConfig) ?? '';
  return directionText
    ? `${directionText} ${speedDisplay}`.trim()
    : speedDisplay;
};

export const formatWindSpeedHeader = (
  value: number,
  unitsConfig: UnitsConfig,
  directionText?: string,
): string => {
  const speedDisplay = formatWindSpeedValueWithUnit(value, unitsConfig) ?? '—';

  if (speedDisplay === '—') {
    return '—';
  }

  return directionText
    ? `${directionText} ${speedDisplay}`.trim()
    : speedDisplay;
};

/** Wind speed label for chart tick rows (full unit suffix, e.g. `13km/h`). */
export const formatWindSpeedChartLabel = (
  value: number,
  unitsConfig: UnitsConfig,
): string => formatWindSpeedValueWithUnit(value, unitsConfig) ?? '';

const WIND_SPEED_CHART_UNIT_LINES: Record<string, string> = {
  kmh: 'km/h',
  mph: 'mph',
  ms: 'm/s',
};

/** Wind speed chart labels as `[value, unitLine]` (e.g. `['13', 'km/h']`). */
export const formatWindSpeedChartLabelLines = (
  value: number,
  unitsConfig: UnitsConfig,
): [string, string] | [string] | null => {
  if (unitsConfig.windSpeed === 'knots') {
    const compact = formatWindSpeedKnotsCompact(value, unitsConfig);
    return compact ? [compact] : null;
  }

  const unit = unitsConfig.windSpeed;
  const unitLine = unit ? WIND_SPEED_CHART_UNIT_LINES[unit] : undefined;
  if (!unitLine) {
    return null;
  }

  const numericValue = roundWindSpeedValue(value, unitsConfig);
  if (numericValue == null) {
    return null;
  }

  return [String(numericValue), unitLine];
};

export const buildWindSpeedValueLabelRow = (
  series: TimeSeriesPoint[],
  unitsConfig: UnitsConfig,
  options:
    | string
    | {
        color?: string;
        textAngle?: number;
        rowHeight?: number;
      } = '#9B9B9B',
): AxisLabelRow => {
  const resolved = typeof options === 'string' ? { color: options } : options;

  return {
    key: 'value',
    color: resolved.color ?? '#9B9B9B',
    textAngle: resolved.textAngle,
    rowHeight: resolved.rowHeight ?? CHART_LAYOUT.MULTI_LINE_ROW_HEIGHT,
    getLabelLines: (tick) =>
      formatWindSpeedChartLabelLines(valueAtTime(series, tick), unitsConfig),
  };
};

/** Swell height is always shown to 1 decimal place (e.g. `1.0m`, `1.2m`). */
export const formatSwellHeightDisplay = (
  swellMetres: number,
  unitsConfig: UnitsConfig,
  directionText?: string,
): string => {
  const numericValue = unitsFormatter(
    swellMetres,
    'swellHeight',
    unitsConfig,
    null,
    true,
  );

  let heightDisplay: string;
  if (typeof numericValue === 'number' && Number.isFinite(numericValue)) {
    const label = getUnitsLabel('swellHeight', unitsConfig);
    heightDisplay = `${numericValue.toFixed(1)}${typeof label === 'string' ? label : ''}`;
  } else {
    heightDisplay =
      unitsFormatter(swellMetres, 'swellHeight', unitsConfig) ?? '';
  }

  return directionText
    ? `${directionText} ${heightDisplay}`.trim()
    : heightDisplay;
};

/** Tide height is shown to 1 decimal place (e.g. `1.2m`). */
export const formatTideHeightDisplay = (
  tideMetres: number,
  unitsConfig: UnitsConfig,
): string => {
  const numericValue = unitsFormatter(
    tideMetres,
    'tideHeight',
    unitsConfig,
    null,
    true,
  );

  if (typeof numericValue !== 'number' || !Number.isFinite(numericValue)) {
    return unitsFormatter(tideMetres, 'tideHeight', unitsConfig) ?? '';
  }

  const label = getUnitsLabel('tideHeight', unitsConfig);
  return `${numericValue.toFixed(1)}${typeof label === 'string' ? label : ''}`;
};

export const coerceUnitLabelSuffix = (
  label: unknown,
  fallback: string,
): string => {
  if (typeof label === 'string') {
    const trimmed = label.trim();
    if (trimmed) {
      return trimmed;
    }
  }

  return fallback;
};

export const getSwellPeriodUnitSuffix = (unitsConfig: UnitsConfig): string =>
  coerceUnitLabelSuffix(getUnitsLabel('swellPeriod', unitsConfig), 's');

export const formatSwellPeriodChartLabel = (
  value: number,
  unitsConfig: UnitsConfig,
): string => {
  if (!Number.isFinite(value)) {
    return '';
  }

  return `${Math.round(value)}${getSwellPeriodUnitSuffix(unitsConfig)}`;
};

export const formatSwellPeriodSecondaryDisplay = (
  value: number,
  unitsConfig: UnitsConfig,
): string | null => {
  if (!Number.isFinite(value)) {
    return null;
  }

  return `${Math.round(value)}${getSwellPeriodUnitSuffix(unitsConfig)} period`;
};

export const formatRainProbabilityDisplay = (probability: number): string => {
  if (!Number.isFinite(probability)) {
    return '';
  }

  return `${Math.round(probability)}%`;
};

export const formatRainProbabilityChartLabel = (probability: number): string =>
  formatRainProbabilityDisplay(probability);

export interface RainHeaderParts {
  primary: string;
}

export const formatRainHeaderParts = (
  probability: number | undefined,
): RainHeaderParts => {
  const primary =
    probability != null && Number.isFinite(probability)
      ? `${formatRainProbabilityDisplay(probability)} chance`
      : '—';

  return { primary };
};

/** WHO UV index exposure categories. */
export const getUvIndexCategory = (uvIndex: number): string => {
  if (!Number.isFinite(uvIndex)) {
    return '';
  }

  const rounded = Math.round(uvIndex);
  if (rounded <= 2) {
    return 'Low';
  }
  if (rounded <= 5) {
    return 'Moderate';
  }
  if (rounded <= 7) {
    return 'High';
  }
  if (rounded <= 10) {
    return 'Very high';
  }
  return 'Extreme';
};

export const formatUvIndexDisplay = (uvIndex: number): string => {
  if (!Number.isFinite(uvIndex)) {
    return '';
  }

  return `${Math.round(uvIndex)}`;
};

export const formatUvIndexChartLabel = (uvIndex: number): string =>
  formatUvIndexDisplay(uvIndex);

export interface UvIndexHeaderParts {
  primary: string;
  secondary: string;
}

export const formatUvIndexHeaderParts = (
  uvIndex: number | undefined,
): UvIndexHeaderParts | null => {
  if (uvIndex == null || !Number.isFinite(uvIndex)) {
    return null;
  }

  const category = getUvIndexCategory(uvIndex);
  if (!category) {
    return null;
  }

  return {
    primary: category,
    secondary: formatUvIndexDisplay(uvIndex),
  };
};

export const formatUvIndexHeader = (uvIndex: number | undefined): string => {
  const parts = formatUvIndexHeaderParts(uvIndex);
  return parts ? `${parts.primary} ${parts.secondary}` : '—';
};

export const formatTemperatureChartLabel = (
  value: number,
  unitsConfig: UnitsConfig,
): string => {
  if (!Number.isFinite(value)) {
    return '';
  }

  const numeric = unitsFormatter(value, 'temperature', unitsConfig, '', true);
  if (numeric === null || numeric === '') {
    return '';
  }

  return `${Math.round(Number(numeric))}°`;
};

export const formatTemperatureHeader = (
  value: number | undefined,
  unitsConfig: UnitsConfig,
): string => {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }

  return formatTemperatureChartLabel(value, unitsConfig);
};

export const getFeelsLikeTemperatureForChartSelection = (
  selectedPoint: EnrichedGraphSeriesPoint | undefined,
): number | null => {
  if (
    selectedPoint?.feelsLike != null &&
    Number.isFinite(selectedPoint.feelsLike)
  ) {
    return selectedPoint.feelsLike;
  }

  return null;
};

export const getForecastDayDomain = getForecastDayChartDomain;
